import { useFavoriteIds, useToggleFavorite } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDeleteDate, PenEdit16, Star16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenEditGoalDialog } from 'shared/contexts/goal-form';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { Goal } from 'shared/types/goal';
import { FavoriteType } from 'shared/types/user-settings';

export type EditListProps = {
  goal: Goal;
  onClose: () => void;
};

export const EditList: React.FC<EditListProps> = ({ goal, onClose }) => {
  const { t } = useTranslation();

  const favoriteIds = useFavoriteIds();
  const isFavorite = useMemo(
    () => favoriteIds.includes(goal.id),
    [favoriteIds, goal.id],
  );

  const openEditDialog = useOpenEditGoalDialog();
  const { mutate: updateGoal } = useUpdateGoalMutation();
  const toggleFavorite = useToggleFavorite();

  const onEditGoal = (e: React.MouseEvent) => {
    e.preventDefault();
    openEditDialog(goal, goal.id);
    onClose();
  };
  const onClearDates = (e: React.MouseEvent) => {
    e.preventDefault();
    updateGoal({ goal: { id: goal.id, startDate: null, deadline: null } });
    onClose();
  };
  const onFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    toggleFavorite(goal.id, FavoriteType.Goal);
    onClose();
  };

  return (
    <PopupMenuList hasBottomBorder>
      {!goal?.frozenAt && !goal?.archivedAt && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onEditGoal}
            start={<Icon icon={PenEdit16} />}
          >
            {t('context-menus.goal.labels.edit')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}

      {(!!goal.startDate || !!goal.deadline) && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onClearDates}
            start={<Icon icon={CalendarDeleteDate} />}
          >
            {t('context-menus.goal.labels.clear-dates')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )}

      <PopupMenuListItem>
        <PopupMenuButton onClick={onFavorite} start={<Icon icon={Star16} />}>
          {t(
            isFavorite
              ? 'context-menus.goal.labels.remove-favorite'
              : 'context-menus.goal.labels.add-favorite',
          )}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
